import type { StoreEngine } from './engine.js';

/**
 * Decision 0029, phase 1: tabs announce that a store moved, not what changed.
 *
 * A same-origin `BroadcastChannel` carrying an **invalidation hint** — the store
 * identity and the revision a commit reached. Never a patch, an object, or an
 * alternative commit path. A receiver goes to persistence for the facts, which
 * is what keeps decision 0027 the correctness boundary rather than this channel:
 *
 * > *"a channel message is a latency optimization and visibility trigger, never
 * > evidence that a particular read or write is current."*
 *
 * So a notice that is **delayed, duplicated, reordered or lost** cannot produce
 * a wrong read. The worst a lost notice does is leave a tab stale until its next
 * write is refused `stale` — which is exactly the behaviour this exists to
 * improve on, not a correctness hole it opens.
 *
 * This is deliberately NOT a third operation on `StorePersistence`. The port
 * stays `load` plus conditional `commit`, so IndexedDB, an HTTP endpoint and
 * Postgres can all still implement one store contract.
 */
export interface TabInvalidation {
  /** Stop announcing and stop listening. Idempotent. */
  close(): void;
  /** The highest revision this tab has been told about, announced or received.
   *  Exposed for a guard rather than for the product. */
  seen(): number;
}

/** What crosses the channel. Two fields, and neither is content. */
interface Notice {
  readonly store: string;
  readonly revision: number;
}

const isNotice = (v: unknown): v is Notice =>
  typeof v === 'object' && v !== null
  && typeof (v as Notice).store === 'string'
  && typeof (v as Notice).revision === 'number';

export interface TabInvalidationOptions {
  readonly engine: StoreEngine;
  /**
   * NAMESPACED BY THE STORE'S DURABLE IDENTITY, which the decision requires
   * *"so unrelated databases and test runs cannot invalidate one another"*.
   * Two suites running side by side under one origin would otherwise each send
   * the other back to reload a database it has never opened.
   */
  readonly store: string;
  /** Injected so a test can drive this without a browser. */
  readonly channel?: BroadcastChannelLike;
  /** Called after a received notice has been reconciled, so a surface can
   *  repaint. Phase 2's seam; phase 1 only has to reach the engine. */
  readonly onRefreshed?: (revision: number) => void;
}

/** The part of `BroadcastChannel` this uses, so a test needs no browser. */
export interface BroadcastChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export function attachTabInvalidation(options: TabInvalidationOptions): TabInvalidation {
  const channel = options.channel
    ?? new BroadcastChannel(`morph:store:${options.store}`) as unknown as BroadcastChannelLike;
  let closed = false;
  let seen = 0;
  /**
   * COALESCED, because a burst of commits is one refresh to the newest state
   * rather than one full load each — the decision says so, and the cost is a
   * complete `load` per message otherwise. `pending` is the highest revision
   * announced while a refresh was already running; the loop drains to it.
   */
  let refreshing: Promise<void> | undefined;
  let pending = 0;

  const drain = async () => {
    /**
     * `finally`, because a refresh that FAILS must not wedge the tab.
     *
     * @Codex, P2 on #166: *"If persistence loading fails once during
     * `hydrate` — for example, from a transient IndexedDB transaction error —
     * `drain()` rejects before clearing `refreshing`. Every later notice then
     * executes `refreshing ??= drain()` against that already-rejected promise,
     * so this tab never refreshes again and also produces an unhandled
     * rejection."*
     *
     * Correct on both counts. `??=` sees a settled promise and keeps it, so one
     * transient error is permanent blindness to every later commit — which is
     * exactly the defect task 0104 exists to remove, reintroduced by its own
     * transport.
     */
    try {
      await pump();
    } finally {
      refreshing = undefined;
    }
  };

  const pump = async () => {
    while (pending > seen) {
      const target = pending;
      // The engine reloads through the ordinary contract. It may come back
      // ABOVE the target if more landed meanwhile, which is fine and is why
      // this compares rather than assigns.
      await options.engine.hydrate({}, true);
      const reached = await options.engine.snapshot();
      seen = Math.max(seen, reached.revision, target);
      options.onRefreshed?.(seen);
    }
  };

  channel.onmessage = (event) => {
    if (closed || !isNotice(event.data)) return;
    // A notice for somebody else's database is not ours to act on. The channel
    // name already separates them; this is the second lock on the same door,
    // because a shared channel is the kind of thing a later refactor merges.
    if (event.data.store !== options.store) return;
    if (event.data.revision <= seen) return;
    pending = Math.max(pending, event.data.revision);
    // A rejected drain is not kept: it clears itself, so the NEXT notice
    // starts a fresh one. The catch is here rather than inside `drain` so a
    // caller that wanted the error could still have it.
    refreshing ??= drain().catch(() => undefined);
  };

  return {
    close: () => {
      if (closed) return;
      closed = true;
      channel.onmessage = null;
      channel.close();
    },
    seen: () => seen,
  };
}

/**
 * The announcing half, for `StoreOptions.onCommitted`.
 *
 * Separate from the listening half because a store may want one without the
 * other — the measurement route wants neither, and a read-only viewer wants
 * only to listen. Keeping them apart also means the announce path has no
 * refresh state to get wrong.
 *
 * **Refreshing never announces.** Only a successful local commit does, which is
 * what stops two tabs notifying each other in a loop for ever.
 */
export function announceOn(
  channel: BroadcastChannelLike, store: string,
): (revision: number) => void {
  return (revision) => { channel.postMessage({ store, revision } satisfies Notice); };
}
